import {
  IWorkout,
  useKeyWorkoutsList,
  workoutsList,
} from '@ifit/gateway-wolf-athenaeum-service';
import { useQuery, UseQueryResult } from 'react-query';
import { useCurrentSoftwareNumber } from '../../../store/software-number';

export interface IEstimatesAndRatings {
  estimates?: IWorkout['estimates'];
  ratings?: IWorkout['ratings'];
}

type PromiseCallbacks = {
  resolve: (estimates: IEstimatesAndRatings) => void;
  reject: (err: Error) => void;
};

let softwareNumber: number;
const batchedIds: string[] = [];
let timeoutId: 0 | NodeJS.Timeout;
const batchTimeout = 1000;
const batchSize = 30;
const promiseMap: Record<string, PromiseCallbacks> = {};

export function useEstimatesAndRating(
  id: string,
  enabled: boolean,
): UseQueryResult<IEstimatesAndRatings> {
  const currentSoftwareNumber = useCurrentSoftwareNumber();
  if (currentSoftwareNumber !== softwareNumber) {
    batchedIds.splice(0, batchedIds.length);
    softwareNumber = currentSoftwareNumber;
  }
  const key: [string, number, string] = [
    useKeyWorkoutsList + 'estimatesAndRatings',
    softwareNumber,
    id,
  ];
  return useQuery(key, queryFn, {
    enabled,
  });
}

function queryFn({
  queryKey,
}: {
  queryKey: [string, number, string];
}): Promise<IEstimatesAndRatings> {
  const [, , id] = queryKey;
  batchedIds.push(id);
  if (!timeoutId) {
    timeoutId = setTimeout(loadBatch, batchTimeout);
  }
  return new Promise<IEstimatesAndRatings>((resolve, reject) => {
    promiseMap[id] = { resolve, reject };
  });
}

async function loadBatch(): Promise<void> {
  const ids = batchedIds.splice(0, batchSize);
  try {
    const workouts = await workoutsList({
      ids,
      softwareNumber,
      doEnableScaling: false,
      returnFields: ['estimates', 'ratings'],
    });

    for (const workout of workouts) {
      if (workout.id) {
        const result = promiseMap[workout.id];
        if (result) {
          result.resolve({
            estimates: workout.estimates,
            ratings: workout.ratings,
          });
        }
        promiseMap[workout.id] = null;
      }
    }
  } catch (err) {
    for (const id of ids) {
      const result = promiseMap[id];
      if (result) {
        result.reject(err);
        promiseMap[id] = null;
      }
    }
  }

  timeoutId = batchedIds.length && setTimeout(loadBatch, batchTimeout);
}
